import { useMemo, useState } from "react";
import { FiExternalLink } from "react-icons/fi";
import { useParams } from "react-router";

import { tenants } from "@/app/tenants/utils";
import { Badge, Button, type BadgeTone } from "@/core/ui/atoms";
import { Banner, PageHeader, SummaryRow } from "@/core/ui/molecules";

import { PlanChangeList } from "../../components/molecules/PlanChangeList";
import { PlanVersionsTab } from "./components/PlanVersionsTab";
import { ResolvedAccessPresetsTab } from "./components/ResolvedAccessPresetsTab";
import { planChanges, plans, type Plan } from "../../utils";

type PlanEditorTab =
  | "capabilities"
  | "models"
  | "catalogs"
  | "limits"
  | "uiconfig"
  | "packs"
  | "savepublish"
  | "versions"
  | "resolved";

type ToggleDefinition = {
  readonly description?: string;
  readonly label: string;
  readonly locked?: boolean;
  readonly source?: string;
  readonly tone?: BadgeTone;
  readonly value: boolean;
};

type TemplateDefinition = {
  readonly detail: string;
  readonly label: string;
  readonly selected: boolean;
};

type TemplatePack = {
  readonly autoInstall: boolean;
  readonly dependencies: readonly string[];
  readonly description: string;
  readonly items: readonly string[];
  readonly name: string;
  readonly version: string;
  readonly vertical: string;
};

type UILabelRow = {
  readonly displayName: string;
  readonly key: string;
  readonly visible: boolean;
};

type EntityLabelRow = {
  readonly key: string;
  readonly plural: string;
  readonly singular: string;
};

type ColumnRow = {
  readonly displayName: string;
  readonly key: string;
  readonly locked?: boolean;
  readonly visible: boolean;
};

const editorTabs = [
  ["capabilities", "Capabilities (A-B-C)"],
  ["models", "Models & Customer (D-E)"],
  ["catalogs", "Catalogs (F-G-H)"],
  ["limits", "Limits & Branding (I-J-K-L)"],
  ["uiconfig", "UI Configuration"],
  ["packs", "Template Packs (N)"],
  ["savepublish", "Save · Publish · Audit (M-O)"],
  ["versions", "Versions"],
  ["resolved", "Resolved Access"],
] as const satisfies readonly [PlanEditorTab, string][];

const uiPreviewRows = {
  customers: [
    {
      accountManager: "Diego Perez",
      email: "owner@acpro.com",
      lastService: "2d ago",
      name: "AC Pro Services",
      phone: "+506 2222-1111",
      type: "Commercial",
    },
    {
      accountManager: "Ethan Reyes",
      email: "carlos@coolbreeze.gt",
      lastService: "1w ago",
      name: "CoolBreeze HVAC",
      phone: "+502 5555-2222",
      type: "Commercial",
    },
  ],
  forms: [
    {
      form: "AC Installation Checklist",
      reviewer: "Diego Perez",
      status: "Reviewed",
      submittedAt: "Apr 27",
      submittedBy: "Roberto Castro",
    },
    {
      form: "Preventive Maintenance Form",
      reviewer: "-",
      status: "Pending",
      submittedAt: "Apr 26",
      submittedBy: "Ana Vargas",
    },
  ],
  workOrders: [
    {
      assignedTo: "Roberto Castro",
      customer: "AC Pro Services",
      dueDate: "Apr 28",
      priority: "High",
      status: "In Progress",
    },
    {
      assignedTo: "Ana Vargas",
      customer: "CoolBreeze HVAC",
      dueDate: "Apr 30",
      priority: "Medium",
      status: "Pending",
    },
  ],
} as const;

const templatePacks: readonly TemplatePack[] = [
  {
    autoInstall: true,
    dependencies: [
      "customer_base",
      "customer_hvac_extension",
      "forms",
      "media",
    ],
    description:
      "Core onboarding for HVAC tenants with customer, equipment, and service records.",
    items: [
      "form: ac_installation_checklist",
      "form: preventive_maintenance",
      "model: customer_hvac_extension",
      "model: equipment_hvac_embedded",
      "media: ac_service_photo_pack",
    ],
    name: "HVAC Starter Pack",
    version: "v2",
    vertical: "HVAC",
  },
  {
    autoInstall: true,
    dependencies: ["customer_base", "customer_pest_extension", "forms"],
    description:
      "Inspection, treatment, and follow-up templates for pest control onboarding.",
    items: [
      "form: treatment_report",
      "form: inspection_sheet",
      "model: customer_pest_extension",
      "media: infestation_reference_pack",
    ],
    name: "Pest Control Core Pack",
    version: "v2",
    vertical: "Pest Control",
  },
  {
    autoInstall: false,
    dependencies: ["customer_base", "forms", "projects"],
    description:
      "Project templates and facility audit assets for larger service operations.",
    items: [
      "form: facility_audit",
      "project: annual_rollout",
      "media: site_walkthrough_pack",
    ],
    name: "Facility Management Pack",
    version: "v1",
    vertical: "Facility Mgmt",
  },
  {
    autoInstall: false,
    dependencies: ["customer_base", "forms", "model_builder"],
    description:
      "Enterprise-grade pack for custom data structures and account workflows.",
    items: [
      "model: telco_account_extension",
      "form: account_upgrade_request",
      "media: service_center_assets",
    ],
    name: "Enterprise Operations Pack",
    version: "v1",
    vertical: "Telco",
  },
] as const;

const formTemplates = [
  {
    detail: "HVAC · 24 fields",
    label: "AC Installation Checklist",
    plans: ["Growth", "Scale", "Enterprise"],
  },
  {
    detail: "Pest Control · 18 fields",
    label: "Treatment Report",
    plans: ["Growth", "Scale", "Enterprise"],
  },
  {
    detail: "Facility Mgmt · 26 fields",
    label: "Facility Audit",
    plans: ["Scale", "Enterprise"],
  },
  {
    detail: "Generic · 14 fields",
    label: "Visit Summary",
    plans: ["Go", "Growth", "Scale", "Enterprise"],
  },
];

const mediaTemplates = [
  {
    detail: "HVAC · 32 assets",
    label: "AC Service Photo Pack",
    plans: ["Scale", "Enterprise"],
  },
  {
    detail: "Facility Mgmt · 12 assets",
    label: "Facility Walkthrough Pack",
    plans: ["Scale", "Enterprise"],
  },
];

const projectTemplates = [
  {
    detail: "HVAC · 14 day rollout",
    label: "Q2 Preventive Campaign",
    plans: ["Scale", "Enterprise"],
  },
  {
    detail: "Generic · 21 day implementation",
    label: "Operational Launch Sprint",
    plans: ["Scale", "Enterprise"],
  },
];

export function PlanDetailPage() {
  const params = useParams();
  const [activeTab, setActiveTab] = useState<PlanEditorTab>("capabilities");
  const plan = plans.find((item) => item.id === params.planId) ?? plans[0];
  const realPlanId = Number(params.planId);
  const hasRealPlan = !Number.isNaN(realPlanId) && realPlanId > 0;
  const planTenants = useMemo(
    () => tenants.filter((tenant) => tenant.plan === plan.name),
    [plan.name],
  );

  return (
    <>
      <PageHeader
        actions={<Button>Actions</Button>}
        breadcrumb={["Super Admin", "Plans", plan.name]}
        subtitle={`${plan.description} - ${planTenants.length} tenants on this plan`}
        title={
          <span className="flex items-center gap-2">
            {plan.name} Plan
            <Badge dot tone="success">
              v{plan.activeVersion} Active
            </Badge>
            {plan.draft ? (
              <Badge tone="pending">Draft v{plan.draft.version}</Badge>
            ) : null}
          </span>
        }
      />

      {plan.draft ? (
        <Banner tone="warning">
          <strong>Draft v{plan.draft.version}</strong> -{" "}
          {plan.draft.unsavedChanges} unsaved changes. Published version v
          {plan.activeVersion} is active for {planTenants.length} tenants.
        </Banner>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2 rounded-panel bg-gray-100 p-1">
        {editorTabs.map(([tabId, label]) => (
          <button
            className={`rounded-control px-3 py-2 text-xs font-medium transition-colors ${
              activeTab === tabId
                ? "bg-surface text-cta shadow-panel"
                : "text-ink-subtle hover:text-ink"
            }`}
            key={tabId}
            onClick={() => setActiveTab(tabId)}
            type="button"
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === "capabilities" ? (
        <CapabilitiesTab plan={plan} planTenants={planTenants} />
      ) : null}
      {activeTab === "models" ? <ModelsTab plan={plan} /> : null}
      {activeTab === "catalogs" ? <CatalogsTab plan={plan} /> : null}
      {activeTab === "limits" ? <LimitsTab plan={plan} /> : null}
      {activeTab === "uiconfig" ? <UIConfigurationTab plan={plan} /> : null}
      {activeTab === "packs" ? <TemplatePacksTab plan={plan} /> : null}
      {activeTab === "savepublish" ? <SavePublishTab plan={plan} /> : null}
      {activeTab === "versions" ? (
        hasRealPlan ? (
          <PlanVersionsTab planId={realPlanId} />
        ) : (
          <Banner>Connect a real plan to view version history.</Banner>
        )
      ) : null}
      {activeTab === "resolved" ? (
        hasRealPlan ? (
          <ResolvedAccessPresetsTab planId={realPlanId} />
        ) : (
          <Banner>Connect a real plan to view resolved access presets.</Banner>
        )
      ) : null}
    </>
  );
}

function CapabilitiesTab({
  plan,
  planTenants,
}: {
  readonly plan: Plan;
  readonly planTenants: readonly (typeof tenants)[number][];
}) {
  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_20rem]">
      <div className="space-y-4">
        <PlanHeaderCard plan={plan} />
        <ToggleSectionCard
          title="B - Modules Included"
          subtitle="Modules control main navigation and access to whole areas in the client app."
          items={getModulesForPlan(plan)}
        />
        <ToggleSectionCard
          title="C - Feature Flags"
          subtitle="Internal features within modules. These refine behavior without changing module structure."
          items={getFeatureFlagsForPlan(plan)}
        />
      </div>
      <aside className="space-y-4">
        <ImpactCard plan={plan} planTenants={planTenants} />
        <TenantListCard planTenants={planTenants} />
      </aside>
    </div>
  );
}

function ModelsTab({ plan }: { readonly plan: Plan }) {
  return (
    <div className="space-y-4">
      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-ink">
              D - Models Configuration
            </h2>
            <p className="mt-1 text-xs text-ink-subtle">
              Structure, usage type, and capabilities are defined in Library.
              This screen controls availability only.
            </p>
          </div>
          <Button>
            <FiExternalLink aria-hidden="true" />
            Open in Library
          </Button>
        </div>
        <div className="space-y-2">
          {getModelsForPlan(plan).map((item) => (
            <ToggleRow key={item.label} item={item} />
          ))}
        </div>

        <div className="mt-6">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-subtle">
            Operational Capabilities (read-only)
          </div>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {["Work Orders / Jobs", "Tasks", "Forms", "Visits / Check-in"].map(
              (label) => (
                <div
                  className="flex items-center justify-between rounded-control border border-border bg-panel-muted px-3 py-2 text-xs opacity-85"
                  key={label}
                >
                  <span className="font-medium text-ink">{label}</span>
                  <Badge tone="success">On</Badge>
                </div>
              ),
            )}
          </div>
        </div>
      </section>

      <Banner tone="info">
        <strong>
          Tenant Setup installs templates while Super Admin only selects them.
        </strong>{" "}
        Installation remains idempotent by template id and template version,
        with no merge behavior in V1.
      </Banner>

      <ToggleSectionCard
        title="E - Customer Visibility"
        subtitle="Customer is a single model. The plan changes which tabs and fields are visible."
        items={getCustomerVisibilityForPlan(plan)}
      />
    </div>
  );
}

function CatalogsTab({ plan }: { readonly plan: Plan }) {
  return (
    <div className="space-y-4">
      <TemplateSelectionCard
        emptyMessage={`${plan.name} does not include Forms Library access yet.`}
        helper={`Mode: ${getFormsMode(plan)}`}
        title="F - Forms Configuration"
        templates={formTemplates.map((template) => ({
          detail: template.detail,
          label: template.label,
          selected: template.plans.includes(plan.name),
        }))}
      />
      <TemplateSelectionCard
        emptyMessage={`Media templates are available in Scale+ only. ${plan.name} tenants do not receive them.`}
        helper={`${mediaTemplates.filter((template) => template.plans.includes(plan.name)).length} selected for ${plan.name}`}
        title="G - Media Templates Configuration"
        templates={mediaTemplates.map((template) => ({
          detail: template.detail,
          label: template.label,
          selected: template.plans.includes(plan.name),
        }))}
      />
      <TemplateSelectionCard
        emptyMessage={`Project templates are available in Scale+ only. ${plan.name} remains focused on daily operations.`}
        helper={`${projectTemplates.filter((template) => template.plans.includes(plan.name)).length} selected for ${plan.name}`}
        title="H - Project Templates Configuration"
        templates={projectTemplates.map((template) => ({
          detail: template.detail,
          label: template.label,
          selected: template.plans.includes(plan.name),
        }))}
      />
    </div>
  );
}

function LimitsTab({ plan }: { readonly plan: Plan }) {
  const limits = getLimitsForPlan(plan);

  return (
    <div className="space-y-4">
      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <h2 className="mb-3 text-sm font-semibold text-ink">I - Limits</h2>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <ConfigField
            helpText="Maximum active seats"
            label="Max Users"
            value={String(limits.users)}
          />
          <ConfigField
            helpText="Workspace count per tenant"
            label="Max Workspaces"
            value={String(limits.workspaces)}
          />
          <ConfigField
            helpText="Storage allowance"
            label="Max Storage (GB)"
            value={String(limits.storage)}
          />
          <ConfigField
            helpText="Default evaluation period"
            label="Trial Days"
            value={String(limits.trial)}
          />
        </div>
      </section>

      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <h2 className="mb-3 text-sm font-semibold text-ink">
          J - Navigation & Menu
        </h2>
        <div className="overflow-hidden rounded-panel border border-border">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="bg-panel-muted text-[10px] uppercase tracking-[0.06em] text-ink-subtle">
              <tr>
                <th className="px-4 py-3 font-semibold">Technical</th>
                <th className="px-4 py-3 font-semibold">Visible</th>
                <th className="px-4 py-3 font-semibold">Display Name</th>
              </tr>
            </thead>
            <tbody>
              {getNavigationRows(plan).map((row) => (
                <tr className="border-t border-border" key={row.key}>
                  <td className="px-4 py-3 font-mono text-xs text-ink-muted">
                    {row.key}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={row.visible ? "success" : "disabled"}>
                      {row.visible ? "Visible" : "Hidden"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-ink">{row.displayName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <V15NoticeCard
        title="K - Tables / Columns Configuration"
        text="Advanced per-table column configuration remains marked for V1.5 to keep the initial launch focused."
      />
      <V15NoticeCard
        title="L - Naming / Commercial Labels"
        text="Mass naming across modules remains a V1.5 candidate. The prototype section is now represented here for continuity."
      />
    </div>
  );
}

function UIConfigurationTab({ plan }: { readonly plan: Plan }) {
  const modules = getUIModuleRows(plan);
  const entities = getUIEntityRows(plan);
  const columns = getUIColumnRows(plan);
  const previewType =
    plan.id === "go"
      ? "workOrders"
      : plan.id === "growth"
        ? "customers"
        : "forms";
  const previewRows = uiPreviewRows[previewType];

  return (
    <div className="space-y-4">
      {plan.draft ? (
        <Banner tone="info">
          <strong>
            {plan.draft.unsavedChanges} unsaved change markers in UI
            Configuration.
          </strong>{" "}
          The prototype treats this as part of the plan draft lifecycle, so the
          section is surfaced here alongside the draft banner.
        </Banner>
      ) : null}

      <Banner tone="info">
        <strong>
          UI Configuration controls how each plan appears to end users.
        </strong>{" "}
        Technical keys never change. Only customer-facing labels and default
        visibility are configured here.
      </Banner>

      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <h2 className="mb-3 text-sm font-semibold text-ink">
          1 - Module Menu Naming
        </h2>
        <div className="overflow-hidden rounded-panel border border-border">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="bg-panel-muted text-[10px] uppercase tracking-[0.06em] text-ink-subtle">
              <tr>
                <th className="px-4 py-3 font-semibold">Technical Module</th>
                <th className="px-4 py-3 font-semibold">Menu Visible</th>
                <th className="px-4 py-3 font-semibold">Display Name</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((row) => (
                <tr className="border-t border-border" key={row.key}>
                  <td className="px-4 py-3 font-mono text-xs text-ink-muted">
                    {row.key}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={row.visible ? "success" : "disabled"}>
                      {row.visible ? "Visible" : "Hidden"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-ink">{row.displayName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <h2 className="mb-3 text-sm font-semibold text-ink">
          2 - Entity / Table Naming
        </h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {entities.map((row) => (
            <div
              className="rounded-control border border-border bg-panel-muted px-3 py-3"
              key={row.key}
            >
              <div className="font-mono text-[11px] text-ink-subtle">
                {row.key}
              </div>
              <div className="mt-2 text-sm font-semibold text-ink">
                {row.singular}
              </div>
              <div className="text-xs text-ink-muted">Plural: {row.plural}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-ink">
              3 - Default Table Columns
            </h2>
            <p className="mt-1 text-xs text-ink-subtle">
              First view users see on each main table. ON/OFF and display name
              only.
            </p>
          </div>
          <Badge tone="info">Preview source: {previewType}</Badge>
        </div>

        <div className="overflow-hidden rounded-panel border border-border">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="bg-panel-muted text-[10px] uppercase tracking-[0.06em] text-ink-subtle">
              <tr>
                <th className="px-4 py-3 font-semibold">Technical Column</th>
                <th className="px-4 py-3 font-semibold">Visible</th>
                <th className="px-4 py-3 font-semibold">Display Name</th>
                <th className="px-4 py-3 font-semibold">Locked</th>
              </tr>
            </thead>
            <tbody>
              {columns.map((row) => (
                <tr className="border-t border-border" key={row.key}>
                  <td className="px-4 py-3 font-mono text-xs text-ink-muted">
                    {row.key}
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={row.visible ? "success" : "disabled"}>
                      {row.visible ? "Visible" : "Hidden"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-ink">{row.displayName}</td>
                  <td className="px-4 py-3">
                    {row.locked ? <Badge tone="warning">Locked</Badge> : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-panel border border-info bg-blue-50 p-4 shadow-panel">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold text-info">
              4 - Preview: {plan.name} Plan
            </h2>
            <p className="mt-1 text-xs text-blue-900">
              How the configured table will look for tenants on this plan.
            </p>
          </div>
          <Badge tone="info">3 visible columns minimum</Badge>
        </div>
        <div className="overflow-hidden rounded-panel border border-blue-200 bg-surface">
          <table className="min-w-full border-collapse text-left text-sm">
            <thead className="bg-panel-muted text-[10px] uppercase tracking-[0.06em] text-ink-subtle">
              <tr>
                {Object.keys(previewRows[0]).map((key) => (
                  <th className="px-4 py-3 font-semibold" key={key}>
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.map((row, index) => (
                <tr className="border-t border-border" key={index}>
                  {Object.entries(row).map(([key, value]) => (
                    <td className="px-4 py-3 text-ink-muted" key={key}>
                      {String(value)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Banner tone="warning">
        <strong>V1 scope remains explicit.</strong> Module and entity labels,
        default visible columns, and preview are included. Drag-and-drop
        ordering, role-based columns, and tenant-level overrides remain out of
        scope.
      </Banner>
    </div>
  );
}

function TemplatePacksTab({ plan }: { readonly plan: Plan }) {
  const packsForPlan = templatePacks.filter((pack) =>
    isPackAvailableForPlan(pack, plan),
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">N - Template Packs</h2>
          <p className="mt-1 text-xs text-ink-subtle">
            {packsForPlan.length} packs available for {plan.name} ·{" "}
            {packsForPlan.filter((pack) => pack.autoInstall).length}{" "}
            auto-install enabled.
          </p>
        </div>
        <Button>
          <FiExternalLink aria-hidden="true" />
          Manage packs in Library
        </Button>
      </div>

      <Banner tone="info">
        <strong>
          This defines which packs are available during tenant creation.
        </strong>{" "}
        The plan controls availability, while tenant setup performs the atomic
        installation.
      </Banner>

      <section className="rounded-panel border border-border bg-panel-muted p-4 shadow-panel">
        <div className="text-center text-sm text-ink-muted">
          <strong className="text-info">Library</strong> creates content ·{" "}
          <strong className="text-cta">Super Admin</strong> controls
          availability · <strong className="text-success">Tenant Setup</strong>{" "}
          installs it atomically.
        </div>
      </section>

      <div className="space-y-4">
        {packsForPlan.map((pack) => (
          <section
            className="rounded-panel border border-border bg-surface p-4 shadow-panel"
            key={`${pack.name}-${pack.version}`}
          >
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-ink">
                    {pack.name}
                  </h3>
                  <Badge tone="neutral">{pack.version}</Badge>
                  <Badge tone="global">Library</Badge>
                </div>
                <p className="mt-1 text-xs leading-5 text-ink-muted">
                  {pack.description}
                </p>
              </div>
              <div className="text-right text-xs text-ink-subtle">
                <div>Auto-install</div>
                <Badge tone={pack.autoInstall ? "success" : "neutral"}>
                  {pack.autoInstall ? "Enabled" : "Optional"}
                </Badge>
              </div>
            </div>

            <div className="mb-3 flex flex-wrap gap-2">
              <Badge tone="info">Vertical: {pack.vertical}</Badge>
              <Badge tone="success">{pack.items.length} items</Badge>
            </div>

            <div className="rounded-control border border-border bg-panel-muted p-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-subtle">
                Installation Sequence
              </div>
              <div className="space-y-2">
                {pack.dependencies.map((dependency, index) => (
                  <div
                    className="flex items-center gap-3 rounded-control border border-border bg-surface px-3 py-2 text-xs"
                    key={`${pack.name}-${dependency}`}
                  >
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-info text-white">
                      {index + 1}
                    </span>
                    <span className="font-mono text-ink-muted">
                      Step {index + 1} - {dependency}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-3">
              <div className="mb-2 text-[10px] font-semibold uppercase tracking-[0.06em] text-ink-subtle">
                Pack Contents
              </div>
              <div className="flex flex-wrap gap-2">
                {pack.items.map((item) => (
                  <span
                    className="rounded border border-border bg-panel-muted px-2 py-1 font-mono text-[10px] text-ink-muted"
                    key={`${pack.name}-${item}`}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </section>
        ))}
      </div>

      <Banner tone="warning">
        <strong>Installation Rules.</strong> Atomic installation, no partial
        success, rollback on failure, idempotent by template id plus version,
        and no merge behavior in V1.
      </Banner>
    </div>
  );
}

function SavePublishTab({ plan }: { readonly plan: Plan }) {
  const relevantChanges = planChanges.filter(
    (change) => change.target === `${plan.name} Plan`,
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
          <h2 className="mb-3 text-sm font-semibold text-ink">
            M - Save & Publish
          </h2>
          {plan.draft ? (
            <>
              <Banner tone="warning">
                <strong>Draft v{plan.draft.version}</strong> ·{" "}
                {plan.draft.unsavedChanges} unsaved changes will affect{" "}
                <strong>{plan.tenantsCount} tenants</strong> when published.
              </Banner>
              <div className="flex flex-wrap gap-2">
                <Button>Save Draft</Button>
                <Button variant="primary">Publish Changes</Button>
                <Button variant="ghost">Discard Draft</Button>
              </div>
            </>
          ) : (
            <p className="text-sm leading-6 text-ink-muted">
              No pending changes. Plan v{plan.activeVersion} was last published
              on {plan.publishedAt} by {plan.publishedBy}.
            </p>
          )}
        </section>

        <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
          <h2 className="mb-3 text-sm font-semibold text-ink">
            Impact Preview
          </h2>
          {plan.draft ? (
            <>
              <SummaryRow label="Tenants affected" value={plan.tenantsCount} />
              <SummaryRow label="Modules changed" value="2 added · 0 removed" />
              <SummaryRow
                label="Feature flags changed"
                value="1 enabled · 0 disabled"
              />
              <SummaryRow
                label="Templates changed"
                value="2 added to allowed list"
              />
              <SummaryRow
                label="Total impact"
                value={`${plan.tenantsCount} tenants on next session`}
              />
            </>
          ) : (
            <p className="text-sm text-ink-muted">No changes to preview.</p>
          )}
        </section>
      </div>

      <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
        <h2 className="mb-3 text-sm font-semibold text-ink">
          O - Audit & Versioning
        </h2>
        {relevantChanges.length > 0 ? (
          <PlanChangeList changes={relevantChanges} />
        ) : (
          <p className="text-sm text-ink-muted">
            No audit entries are available for this plan yet.
          </p>
        )}
      </section>
    </div>
  );
}

function PlanHeaderCard({ plan }: { readonly plan: Plan }) {
  return (
    <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
      <h2 className="mb-3 text-sm font-semibold text-ink">A - Plan Header</h2>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <ConfigField
          helpText="Customer-facing plan name"
          label="Plan Name"
          value={plan.name}
        />
        <ConfigField
          helpText="Publishing state"
          label="Status"
          value="Active"
        />
        <ConfigField
          helpText="Internal positioning"
          label="Internal Description"
          value={plan.description}
        />
        <ConfigField
          helpText="Stripe catalog reference"
          label="Stripe Product ID"
          value={plan.stripeProductId}
        />
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <ConfigField
          helpText="Monthly billing"
          label="Monthly Price (USD)"
          value={plan.priceLabel}
        />
        <ConfigField
          helpText="Published by"
          label="Last Published By"
          value={plan.publishedBy}
        />
      </div>
      <div className="mt-4 rounded-control border border-border bg-panel-muted p-3 text-[13px] leading-6 text-ink-muted">
        {plan.commercial}
      </div>
    </section>
  );
}

function ToggleSectionCard({
  items,
  subtitle,
  title,
}: {
  readonly items: readonly ToggleDefinition[];
  readonly subtitle: string;
  readonly title: string;
}) {
  return (
    <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
      <h2 className="text-sm font-semibold text-ink">{title}</h2>
      <p className="mb-3 mt-1 text-xs text-ink-subtle">{subtitle}</p>
      <div className="space-y-2">
        {items.map((item) => (
          <ToggleRow item={item} key={item.label} />
        ))}
      </div>
    </section>
  );
}

function ToggleRow({ item }: { readonly item: ToggleDefinition }) {
  return (
    <div
      className={`rounded-control border px-3 py-3 ${
        item.locked
          ? "border-warning bg-row-warning"
          : "border-border bg-panel-muted"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-semibold text-ink">{item.label}</div>
          {item.description ? (
            <div className="mt-1 text-xs leading-5 text-ink-muted">
              {item.description}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          {item.source ? <Badge tone="global">{item.source}</Badge> : null}
          {item.locked ? <Badge tone="warning">Locked</Badge> : null}
          <Badge tone={item.value ? (item.tone ?? "success") : "disabled"}>
            {item.value ? "On" : "Off"}
          </Badge>
        </div>
      </div>
    </div>
  );
}

function ImpactCard({
  plan,
  planTenants,
}: {
  readonly plan: Plan;
  readonly planTenants: readonly (typeof tenants)[number][];
}) {
  return (
    <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
      <h2 className="mb-3 text-sm font-semibold text-ink">Impact</h2>
      <SummaryRow label="Tenants affected" value={planTenants.length} />
      <SummaryRow label="Published version" value={`v${plan.activeVersion}`} />
      <SummaryRow label="Price" value={plan.priceLabel} />
      <SummaryRow label="Stripe product" value={plan.stripeProductId} />
    </section>
  );
}

function TenantListCard({
  planTenants,
}: {
  readonly planTenants: readonly (typeof tenants)[number][];
}) {
  return (
    <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
      <h2 className="mb-3 text-sm font-semibold text-ink">Tenants</h2>
      <div className="space-y-2">
        {planTenants.slice(0, 8).map((tenant) => (
          <div
            className="flex items-center justify-between gap-3 text-xs"
            key={tenant.id}
          >
            <span className="font-medium text-ink">{tenant.name}</span>
            <span className="text-ink-subtle">{tenant.countryCode}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function TemplateSelectionCard({
  emptyMessage,
  helper,
  templates,
  title,
}: {
  readonly emptyMessage: string;
  readonly helper: string;
  readonly templates: readonly TemplateDefinition[];
  readonly title: string;
}) {
  const selected = templates.filter((template) => template.selected);

  return (
    <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          <p className="mt-1 text-xs text-ink-subtle">{helper}</p>
        </div>
        <Badge tone="info">{selected.length} selected</Badge>
      </div>
      {selected.length === 0 ? (
        <div className="rounded-panel border border-border bg-panel-muted px-4 py-6 text-center text-sm text-ink-muted">
          {emptyMessage}
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((template) => (
            <div
              className="flex items-center justify-between gap-3 rounded-control border border-border bg-panel-muted px-3 py-3 text-xs"
              key={template.label}
            >
              <div>
                <div className="font-semibold text-ink">{template.label}</div>
                <div className="mt-1 text-ink-muted">{template.detail}</div>
              </div>
              <Badge tone={template.selected ? "success" : "disabled"}>
                {template.selected ? "Allowed" : "Off"}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function V15NoticeCard({
  text,
  title,
}: {
  readonly text: string;
  readonly title: string;
}) {
  return (
    <section className="rounded-panel border border-border bg-surface p-4 shadow-panel">
      <div className="mb-2 flex items-center gap-2">
        <h2 className="text-sm font-semibold text-ink">{title}</h2>
        <Badge tone="pending">V1.5 candidate</Badge>
      </div>
      <p className="text-sm leading-6 text-ink-muted">{text}</p>
    </section>
  );
}

function ConfigField({
  helpText,
  label,
  value,
}: {
  readonly helpText: string;
  readonly label: string;
  readonly value: string;
}) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-ink-muted">{label}</div>
      <div className="rounded-control border border-border bg-panel-muted px-3 py-2 text-sm text-ink">
        {value}
      </div>
      <div className="mt-1 text-[11px] text-ink-subtle">{helpText}</div>
    </div>
  );
}

function getModulesForPlan(plan: Plan): readonly ToggleDefinition[] {
  const isScale = plan.id === "scale" || plan.id === "enterprise";

  return [
    { label: "Jobs / Work Orders", value: true },
    { label: "Schedule", value: true },
    { label: "Customers", value: true },
    { label: "Invoicing", value: true },
    { label: "Forms", value: true },
    { label: "Inventory", value: isScale },
    { label: "Maintenance", value: isScale },
    { label: "Projects", value: isScale },
    { label: "Assets Advanced", value: plan.id === "enterprise" },
    { label: "Documents", value: isScale },
    { label: "Notifications", value: true },
  ] as const;
}

function getFeatureFlagsForPlan(plan: Plan): readonly ToggleDefinition[] {
  const isScale = plan.id === "scale" || plan.id === "enterprise";

  return [
    { label: "Embedded Job Report", value: true },
    { label: "Timesheet", value: true },
    { label: "Forms Library", value: plan.id !== "go" },
    { label: "Forms Editable", value: isScale },
    { label: "Form Builder", value: plan.id === "enterprise" },
    { label: "Media Templates Assignable", value: isScale },
    { label: "Project Templates", value: isScale },
    { label: "Model Builder", value: plan.id === "enterprise" },
    { label: "Inventory Check", value: isScale },
    { label: "Stripe Payments", value: isScale },
  ] as const;
}

function getModelsForPlan(plan: Plan): readonly ToggleDefinition[] {
  const isScale = plan.id === "scale" || plan.id === "enterprise";

  return [
    {
      description: "Core structure · always required",
      label: "Customer Base",
      locked: true,
      source: "Library",
      tone: "success",
      value: true,
    },
    {
      description: "HVAC-specific fields embedded in Customer",
      label: "Customer HVAC Extension",
      source: "Library",
      value: true,
    },
    {
      description: "Equipment as Customer sub-section",
      label: "Equipment HVAC (embedded)",
      source: "Library",
      value: true,
    },
    {
      description: "Equipment as independent asset · Scale+",
      label: "Equipment HVAC (standalone)",
      source: "Library",
      value: isScale,
    },
  ] as const;
}

function getCustomerVisibilityForPlan(plan: Plan): readonly ToggleDefinition[] {
  const isScale = plan.id === "scale" || plan.id === "enterprise";

  return [
    { label: "Basic Info", locked: true, value: true },
    { label: "Contacts", value: true },
    { label: "Locations", value: true },
    { label: "Equipment Info", value: true },
    { label: "Documents", value: isScale },
    { label: "Work History (Advanced)", value: isScale },
    { label: "Billing / Invoicing", value: true },
    { label: "Expirations", value: isScale },
  ] as const;
}

function getFormsMode(plan: Plan) {
  return {
    enterprise: "Full Builder",
    go: "None",
    growth: "Library Read-only",
    scale: "Editable Limited",
  }[plan.id];
}

function getLimitsForPlan(plan: Plan) {
  return {
    enterprise: {
      storage: 500,
      trial: 30,
      users: "Unlimited",
      workspaces: "Unlimited",
    },
    go: { storage: 5, trial: 14, users: 3, workspaces: 1 },
    growth: { storage: 25, trial: 14, users: 25, workspaces: 1 },
    scale: { storage: 100, trial: 14, users: 100, workspaces: 5 },
  }[plan.id];
}

function getNavigationRows(plan: Plan): readonly UILabelRow[] {
  const isScale = plan.id === "scale" || plan.id === "enterprise";

  return [
    {
      displayName: plan.id === "enterprise" ? "Work Orders" : "Jobs",
      key: "work_orders",
      visible: true,
    },
    { displayName: "Schedule", key: "scheduler", visible: true },
    { displayName: "Customers", key: "customers", visible: true },
    { displayName: "Forms", key: "forms", visible: plan.id !== "go" },
    { displayName: "Inventory", key: "inventory", visible: isScale },
    { displayName: "Maintenance", key: "maintenance", visible: isScale },
  ] as const;
}

function getUIModuleRows(plan: Plan): readonly UILabelRow[] {
  const navigationRows = getNavigationRows(plan);

  return [
    ...navigationRows,
    { displayName: "Invoicing", key: "invoicing", visible: true },
    {
      displayName: "Projects",
      key: "projects",
      visible: plan.id === "scale" || plan.id === "enterprise",
    },
  ] as const;
}

function getUIEntityRows(plan: Plan): readonly EntityLabelRow[] {
  return [
    {
      key: "work_order",
      plural: plan.id === "enterprise" ? "Work Orders" : "Jobs",
      singular: plan.id === "enterprise" ? "Work Order" : "Job",
    },
    { key: "customer", plural: "Customers", singular: "Customer" },
    { key: "form_submission", plural: "Forms", singular: "Form" },
    {
      key: "project",
      plural: "Projects",
      singular: "Project",
    },
  ] as const;
}

function getUIColumnRows(plan: Plan): readonly ColumnRow[] {
  const isScale = plan.id === "scale" || plan.id === "enterprise";

  return [
    {
      displayName: plan.id === "enterprise" ? "Work Order" : "Job",
      key: "name",
      locked: true,
      visible: true,
    },
    {
      displayName: "Customer",
      key: "customer",
      locked: true,
      visible: true,
    },
    { displayName: "Status", key: "status", locked: true, visible: true },
    {
      displayName: "Assigned To",
      key: "assigned_to",
      visible: true,
    },
    { displayName: "Due Date", key: "due_date", visible: true },
    { displayName: "Priority", key: "priority", visible: isScale },
  ] as const;
}

function isPackAvailableForPlan(pack: TemplatePack, plan: Plan) {
  if (pack.vertical === "Telco") {
    return plan.id === "enterprise";
  }

  if (pack.vertical === "Facility Mgmt") {
    return plan.id === "scale" || plan.id === "enterprise";
  }

  if (pack.vertical === "HVAC" || pack.vertical === "Pest Control") {
    return plan.id !== "go";
  }

  return true;
}
